import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Clock, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { blogPosts, getFeaturedPosts } from '../data/portfolio';

const categories = ['All', 'Branding', 'Video', 'Digital', 'Content', 'Agency'];

// Blog Card Component
const BlogCard = ({ post, index, featured = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, amount: 0.3 });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (featured) {
    return (
      <motion.article
        ref={cardRef}
        initial={{ opacity: 0, y: 60 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Link to={`/blog/${post.id}`} className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div className="relative aspect-[4/3] lg:aspect-[16/12] overflow-hidden rounded-2xl">
            <motion.img
              src={post.image}
              alt={post.title}
              className="absolute inset-0 w-full h-full object-cover"
              animate={{ scale: isHovered ? 1.1 : 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            />
            <motion.div 
              className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"
              animate={{ opacity: isHovered ? 0.8 : 0.5 }}
            />
            <motion.div
              className="absolute top-6 left-6 px-4 py-2 bg-white text-black text-sm font-bold rounded-full"
              animate={{ scale: isHovered ? 1.05 : 1 }}
            >
              Featured
            </motion.div>
          </div>
          
          <div className="space-y-6">
            <div className="flex items-center gap-4 text-white/50 text-sm">
              <span className="uppercase tracking-wider">{post.category}</span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Syne'] leading-tight group-hover:text-orange-400 transition-colors">
              {post.title}
            </h2>
            
            <p className="text-white/60 text-lg leading-relaxed">
              {post.excerpt}
            </p>
            
            <div className="flex items-center justify-between pt-4">
              <div className="flex items-center gap-4">
                <img
                  src={post.author.avatar}
                  alt={post.author.name}
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-white/20"
                />
                <div>
                  <p className="font-medium text-white">{post.author.name}</p>
                  <p className="text-white/50 text-sm">{formatDate(post.publishedAt)}</p>
                </div>
              </div>
              
              <motion.div
                className="flex items-center gap-2 text-orange-400"
                animate={{ x: isHovered ? 5 : 0 }}
              >
                <span className="font-medium">Read Article</span>
                <ArrowRight className="w-5 h-5" />
              </motion.div>
            </div>
          </div>
        </Link>
      </motion.article>
    );
  }

  return (
    <motion.article
      ref={cardRef}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={`/blog/${post.id}`}>
        <div className="relative aspect-[4/3] overflow-hidden rounded-2xl mb-6">
          <motion.img
            src={post.image}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover"
            animate={{ scale: isHovered ? 1.1 : 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          />
          <motion.div 
            className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"
            animate={{ opacity: isHovered ? 0.9 : 0.7 }}
          />
          
          {/* Hover overlay with arrow */}
          <motion.div
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white flex items-center justify-center"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: isHovered ? 1 : 0, rotate: isHovered ? 0 : -180 }}
            transition={{ duration: 0.3 }}
          >
            <ArrowUpRight className="w-5 h-5 text-black" />
          </motion.div>

          {/* Category */}
          <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-white/10 backdrop-blur-sm text-white text-xs font-medium uppercase tracking-wider rounded-full">
            {post.category}
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-white/50 text-sm">
            <span>{formatDate(post.publishedAt)}</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>{post.readTime}</span>
          </div>
          
          <h3 className="text-xl md:text-2xl font-bold font-['Syne'] group-hover:text-orange-400 transition-colors leading-tight">
            {post.title}
          </h3>
          
          <p className="text-white/50 text-sm line-clamp-2">
            {post.excerpt}
          </p>
        </div>
      </Link>
    </motion.article>
  );
};

const BlogPage = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const featuredPosts = getFeaturedPosts();
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  const filteredPosts = activeFilter === 'All'
    ? blogPosts
    : blogPosts.filter(p => p.category === activeFilter);

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO 
        title="Blog"
        description="Insights, trends, and stories from the Bankki Media team."
        url="/blog"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section ref={heroRef} className="relative h-screen flex items-center justify-center overflow-hidden">
          <motion.div style={{ y }} className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black z-10" />
            <img
              src="https://customer-assets.emergentagent.com/job_4c9ec105-7961-43e0-9222-2e0dd1f1cf63/artifacts/8tpw61mu_Screenshot%202026-02-22%20135727.png"
              alt="Blog"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>

          <motion.div style={{ opacity }} className="relative z-20 text-center px-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-white/60 text-sm uppercase tracking-[0.3em] mb-6"
            >
              Our Blog
            </motion.p>
            
            <div className="overflow-hidden">
              <motion.h1
                initial={{ y: 120 }}
                animate={isHeroInView ? { y: 0 } : {}}
                transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-bold font-['Syne'] tracking-tight leading-[0.9]"
              >
                Insights
              </motion.h1>
            </div>
            <div className="overflow-hidden">
              <motion.h1
                initial={{ y: 120 }}
                animate={isHeroInView ? { y: 0 } : {}}
                transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-bold font-['Syne'] tracking-tight leading-[0.9] text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-orange-400"
              >
                & Stories.
              </motion.h1>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mt-8"
            >
              Thoughts on creativity, strategy, and the ever-evolving landscape of branding and marketing.
            </motion.p>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="flex flex-col items-center gap-2 text-white/50"
            >
              <span className="text-xs uppercase tracking-widest">Scroll</span>
              <div className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent" />
            </motion.div>
          </motion.div>
        </section>

        {/* Featured Post */}
        {featuredPosts[0] && (
          <section className="py-24 md:py-32 bg-black border-b border-white/10">
            <div className="container-custom">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-white/50 text-sm uppercase tracking-[0.3em] mb-8 flex items-center gap-4"
              >
                <span className="w-12 h-px bg-white/30" />
                Featured Article
              </motion.p>
              <BlogCard post={featuredPosts[0]} index={0} featured />
            </div>
          </section>
        )}

        {/* Filter */}
        <section className="py-8 border-b border-white/10 sticky top-20 bg-black/95 backdrop-blur-lg z-30">
          <div className="container-custom">
            <div className="flex flex-wrap gap-3">
              {categories.map((cat) => (
                <motion.button
                  key={cat}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveFilter(cat)}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                    activeFilter === cat
                      ? 'bg-white text-black'
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10'
                  }`}
                >
                  {cat}
                </motion.button>
              ))}
            </div>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="py-24 md:py-32">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
              {filteredPosts.map((post, index) => (
                <BlogCard key={post.id} post={post} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-24 md:py-32 bg-gradient-to-b from-black to-gray-900 border-t border-white/10">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto"
            >
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] mb-6">
                Stay{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
                  inspired
                </span>
              </h2>
              <p className="text-white/60 text-lg mb-10">
                Subscribe to our newsletter for the latest insights, trends, and creative inspiration.
              </p>
              <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-6 py-4 bg-white/5 border border-white/10 rounded-full text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition-colors"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-white text-black font-bold rounded-full hover:bg-orange-400 hover:text-white transition-all"
                >
                  Subscribe
                </motion.button>
              </form>
            </motion.div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPage;
